import { PrescricaoType, TipoReceitaSncr } from '_metronic/helpers/enums'
import { isControleEspecial, tipoReceitaSncrDoItem } from '../receitaControlada'
import { ItemPrescricao, rotuloCatalogo } from './itens'

/** Tipo de documento que um grupo de itens vira ao assinar. */
export type TipoDocumento = 'simples' | 'controle' | 'formula' | 'exame' | 'orientacao'

export const ORDEM_TIPOS_DOCUMENTO: TipoDocumento[] = [
  'simples',
  'controle',
  'formula',
  'exame',
  'orientacao',
]

/** Pedido de exames de item sem tipo cadastrado (o backend agrupa o `tipo` nulo como "geral"). */
export const TIPO_EXAME_GERAL = 'Geral'

export const VALIDADE_RETENCAO_DIAS = 10
export const VALIDADE_CONTROLE_DIAS = 30

/** Documento que a montagem vai gerar, calculado no cliente antes de gravar qualquer coisa. */
export type DocumentoPrevisto = {
  chave: string
  tipo: TipoDocumento
  /** Medicamento da receita de controle especial, ou tipo do pedido de exames. */
  complemento?: string
  itens: ItemPrescricao[]
  validadeDias?: number
  sncr?: TipoReceitaSncr
  /** Itens de retenção que mudam a validade e a numeração da receita simples. */
  itensRetencao: ItemPrescricao[]
}

const tipoExameDo = (item: ItemPrescricao) => item.catalogo.tipoExame?.trim() || TIPO_EXAME_GERAL

/**
 * Espelha a divisão do backend (`PrescricaoServiceImpl.save`): uma receita simples com os
 * medicamentos comuns (vira RET e 10 dias se tiver retenção), uma receita de controle especial
 * por medicamento controlado (RCE, 30 dias), uma de fórmulas, um pedido de exames por tipo e
 * uma de orientações. O backend continua sendo quem decide; isto serve para a prévia.
 */
export const agruparEmDocumentos = (itens: ItemPrescricao[]): DocumentoPrevisto[] => {
  const documentos: DocumentoPrevisto[] = []
  const medicamentos = itens.filter((item) => item.categoria === PrescricaoType.Medicamento)
  const sncrDo = (item: ItemPrescricao) =>
    tipoReceitaSncrDoItem(PrescricaoType.Medicamento, item.catalogo)

  // Controle especial sai em receita própria: RCE consome número do bloco, notificação consome número avulso do tipo dela.
  const controlado = (item: ItemPrescricao) => isControleEspecial(item.catalogo)
  const comuns = medicamentos.filter((item) => !controlado(item))
  if (comuns.length > 0) {
    const retencao = comuns.filter((item) => sncrDo(item) === TipoReceitaSncr.RET)
    documentos.push({
      chave: 'simples',
      tipo: 'simples',
      itens: comuns,
      itensRetencao: retencao,
      ...(retencao.length > 0 && {
        validadeDias: VALIDADE_RETENCAO_DIAS,
        sncr: TipoReceitaSncr.RET,
      }),
    })
  }

  medicamentos.filter(controlado).forEach((item) =>
    documentos.push({
      chave: `controle-${item.chave}`,
      tipo: 'controle',
      complemento: rotuloCatalogo(item.catalogo),
      itens: [item],
      itensRetencao: [],
      validadeDias: VALIDADE_CONTROLE_DIAS,
      sncr: sncrDo(item),
    })
  )

  const formulas = itens.filter((item) => item.categoria === PrescricaoType.Formula)
  if (formulas.length > 0) {
    documentos.push({ chave: 'formula', tipo: 'formula', itens: formulas, itensRetencao: [] })
  }

  const exames = itens.filter((item) => item.categoria === PrescricaoType.Exame)
  const tiposExame = Array.from(new Set(exames.map(tipoExameDo)))
  tiposExame.forEach((tipoExame) =>
    documentos.push({
      chave: `exame-${tipoExame}`,
      tipo: 'exame',
      complemento: tipoExame,
      itens: exames.filter((item) => tipoExameDo(item) === tipoExame),
      itensRetencao: [],
    })
  )

  const orientacoes = itens.filter((item) => item.categoria === PrescricaoType.Orientacoes)
  if (orientacoes.length > 0) {
    documentos.push({
      chave: 'orientacao',
      tipo: 'orientacao',
      itens: orientacoes,
      itensRetencao: [],
    })
  }

  return documentos
}

/** Quantos números SNCR de cada tipo os documentos vão consumir. */
export const necessidadesSncr = (
  documentos: DocumentoPrevisto[]
): Partial<Record<TipoReceitaSncr, number>> =>
  documentos.reduce<Partial<Record<TipoReceitaSncr, number>>>((acc, documento) => {
    if (documento.sncr) acc[documento.sncr] = (acc[documento.sncr] ?? 0) + 1
    return acc
  }, {})

/** Número de exibição de cada item (1, 2, 3…) na ordem dos documentos; orientações não contam. */
export const numeracaoDosItens = (documentos: DocumentoPrevisto[]): Map<string, number> => {
  const numeros = new Map<string, number>()
  documentos
    .flatMap((documento) => documento.itens)
    .filter((item) => item.categoria !== PrescricaoType.Orientacoes)
    .forEach((item, indice) => numeros.set(item.chave, indice + 1))
  return numeros
}
